import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GetAllAdminPost } from "../services/AdminPostService";
import { setErrorMessage } from "../slices/messageSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { format, parseISO } from "date-fns";
import { vi } from "date-fns/locale";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { staticRanges } from "../components/users/UserList/UserFilters";

const ITEM_PER_PAGE = 20;

const formatDateOnly = (dateString) => {
  if (!dateString) return "";
  try {
    const date =
      typeof dateString === "string" ? parseISO(dateString) : dateString;
    return format(date, "yyyy-MM-dd", { locale: vi });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

const ManageAdminPost = () => {
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    dateRelease: "",
    createdAtForm: "",
    createdAtTo: "",
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerType, setDatePickerType] = useState(null);
  const datePickerRef = useRef(null);
  const buttonRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Đóng picker khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return "Chọn khoảng thời gian";
    return `${format(new Date(startDate), "dd/MM/yyyy", {
      locale: vi,
    })} - ${format(new Date(endDate), "dd/MM/yyyy", { locale: vi })}`;
  };

  const handleDateRangeSelect = (ranges) => {
    const { startDate, endDate } = ranges.selection;
    if (datePickerType === "createdAt") {
      setFilters((prev) => ({
        ...prev,
        createdAtForm: startDate ? format(startDate, "yyyy-MM-dd") : "",
        createdAtTo: endDate ? format(endDate, "yyyy-MM-dd") : "",
      }));
    }
    setShowDatePicker(false);
  };

  
  const DateRangeButton = ({ type, label, startDate, endDate }) => {
    const selectionRange = {
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
      key: "selection",
    };

    return (
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => {
            if (datePickerType === type && showDatePicker) {
              setShowDatePicker(false);
            } else {
              setDatePickerType(type);
              setShowDatePicker(true);
            }
          }}
          className="w-full border rounded-lg p-2.5 pl-4 text-left bg-white dark:bg-gray-700 dark:text-white hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {formatDateRange(startDate, endDate)}
        </button>
        {showDatePicker && datePickerType === type && (
          <div
            ref={datePickerRef}
            className="absolute z-50 mt-1 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700"
            style={{ minWidth: "600px" }}
          >
            {/* Chèn CSS override ngay trong JSX để ép luôn picker thành light theme */}
            <style>
              {`
               /* Ép background của calendar luôn trắng */
               .rdrCalendarWrapper, 
               .rdrMonthAndYearWrapper, 
               .rdrDateDisplayWrapper, 
               .rdrMonthPicker, 
               .rdrYearPicker {
                 background: white !important;
                 color: #111 !important;
               }
               .rdrDefinedRangesWrapper, 
               .rdrStaticRange, 
               .rdrStaticRangeLabel {
                 background: white !important;
                 color: #111 !important;
               }
               /* Border và hover */
               .rdrDefinedRangesWrapper {
                 border-right: 1px solid #e5e7eb !important; /* gray-200 */
               }
               .rdrStaticRange:hover {
                 background: #f3f4f6 !important; /* gray-100 */
               }
               .rdrStaticRangeSelected {
                 background: #3b82f6 !important; /* blue-500 */
               }
               .rdrStaticRangeSelected .rdrStaticRangeLabel {
                 color: white !important;
               }
               /* Ngày trong calendar */
               .rdrDayNumber {
                 color: #111 !important;
               }
               .rdrDayToday .rdrDayNumber span:after {
                 background: #3b82f6 !important;
               }
               .rdrInRange, 
               .rdrStartEdge, 
               .rdrEndEdge {
                 background: #3b82f6 !important;
               }
               .rdrDayHovered {
                 border-color: #3b82f6 !important;
               }
             `}
            </style>

            <DateRangePicker
              onChange={handleDateRangeSelect}
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={2}
              direction="horizontal"
              locale={vi}
              rangeColors={["#3B82F6"]}
              showDateDisplay={false}
              staticRanges={staticRanges}
              inputRanges={[]}
              ranges={[selectionRange]}
            />
          </div>
        )}
        <span className="absolute -top-2 left-2 text-xs bg-white dark:bg-gray-900 px-1">
          {label}
        </span>
      </div>
    );
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchAdminPosts = async () => {
    setLoading(true);
    try {
      const response = await GetAllAdminPost(
        filters.createdAtForm,
        filters.createdAtTo,
        filters.dateRelease,
        page,
        ITEM_PER_PAGE
      );
      if (response.status_code == 200) {
        setPosts(response.data);
      } else {
        dispatch(setErrorMessage(response.message));
      }
    } catch (error) {
      dispatch(setErrorMessage("Có lỗi xảy ra khi lấy dữ liệu"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminPosts();
  }, [filters, page]);

  const resetFilter = () => {
    setFilters({
      dateRelease: "",
      createdAtForm: "",
      createdAtTo: "",
    });
  };
  return (
    <div className="flex justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-6">
      <div className="max-w-[1200px] w-full flex flex-col bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
          Quản lý Thời Gian Đăng Bài
        </h2>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-4 gap-4">
          <div className="relative">
            <input
              type="date"
              name="dateRelease"
              value={filters.dateRelease}
              onChange={handleFilterChange}
              className="w-full border rounded-lg p-2.5 pl-4 dark:bg-gray-700 dark:text-white"
            />
            <span className="absolute -top-2 left-2 text-xs bg-white dark:bg-gray-900 px-1">
              Ngày phát hành
            </span>
          </div>

          <DateRangeButton
            type="createdAt"
            label="Ngày tạo bài đăng"
            startDate={filters.createdAtForm}
            endDate={filters.createdAtTo}
          />

          <div className="flex">
            <button
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              onClick={resetFilter}
            >
              Reset Filter
            </button>
          </div>

          <div className="flex justify-end">
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              onClick={() => navigate("/create-post")}
            >
              Tạo Bài Đăng
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-300">
            Đang tải dữ liệu...
          </div>
        ) : posts && posts.data && posts.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <thead className="bg-gray-50 dark:bg-gray-700 text-center">
                <tr>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    ID
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Tiêu đề
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Nội dung
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Trạng thái
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Thời gian phát hành
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Ngày tạo
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Ngày cập nhật
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Hoạt Động
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {posts.data.map((post) => (
                  <tr
                    key={post.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-center">
                      {post.id}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                      {post.title}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white max-w-xs truncate">
                      {post.content}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-center">
                      {post.isShown ? "Hoạt động" : "Ẩn"}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-center">
                      {post.timeRelease}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-center">
                      {post.createdAt}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-center">
                      {post.updatedAt}
                    </td>
                    <td
                      className="px-4 py-2 text-sm text-gray-900 dark:text-white text-center cursor-pointer"
                      onClick={() => navigate(`/edit-post/${post.id}`)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 italic text-xl">
            Không có dữ liệu
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageAdminPost;
